import {
  SerializedCapTableTransactionType,
  SerializedStockClass,
} from '@commenda/types/src/cap-table/types';
import { getCorporationName, getPrintableDirectorName } from '@commenda/utils';
import { Prisma, StakeholderType } from '@prisma/client';
import { TShareholderVsSharePercentageMap } from '../../EquityInfo/types';

const calculateStakeholdersEquityMap = ({
  capTableTransactions,
  stockClass,
}: {
  capTableTransactions: SerializedCapTableTransactionType[];
  stockClass: SerializedStockClass;
}) => {
  const dilutedShareCount = capTableTransactions?.reduce(
    (acc, curr) => acc + curr.additionalInformation.quantity,
    0
  );

  const authorizedShares =
    stockClass?.additionalInformation?.authorizedShares;
  const leftShares = authorizedShares - dilutedShareCount;
  const shareRetained =
    leftShares > 0 && capTableTransactions?.length > 0
      ? Number(((leftShares / authorizedShares) * 100).toFixed(4))
      : 0;
  const stakeholdersEquityMap: TShareholderVsSharePercentageMap = {};

  // Keep track of total for adjustment
  let runningTotal = new Prisma.Decimal(0);

  const validTransactions = capTableTransactions.filter((transaction) => {
    const stakeholder = transaction.stakeholding?.stakeholder;
    if (!stakeholder) return false;

    if (stakeholder.corporationId) {
      return !!stakeholder.corporation;
    } else {
      return !!stakeholder.director;
    }
  });

  // Sort transactions by quantity in descending order to handle rounding for largest holdings first
  const sortedTransactions = [...validTransactions].sort(
    (a, b) =>
      b.additionalInformation.quantity - a.additionalInformation.quantity
  );

  const validDilutedShareCount =
    validTransactions?.reduce(
      (acc, curr) => acc + curr.additionalInformation.quantity,
      0
    ) || 0;

  // Process all except the last transaction
  sortedTransactions.forEach((transaction, index) => {
    const type = transaction.stakeholding.stakeholder.corporationId
      ? StakeholderType.INSTITUTION
      : StakeholderType.INDIVIDUAL;

    const id =
      type === StakeholderType.INSTITUTION
        ? transaction.stakeholding.stakeholder.corporationId
        : transaction.stakeholding.stakeholder.directorId;

    const name =
      type === 'INDIVIDUAL'
        ? getPrintableDirectorName(
            transaction.stakeholding.stakeholder.director
          )
        : getCorporationName(transaction.stakeholding.stakeholder.corporation);

    const transactionQuantity = transaction.additionalInformation.quantity
      ? new Prisma.Decimal(transaction.additionalInformation.quantity)
      : new Prisma.Decimal(0);

    // Calculate percentage with 4 decimal precision
    const percentage =
      validDilutedShareCount > 0
        ? transactionQuantity
            .mul(100)
            .div(validDilutedShareCount)
            .toDecimalPlaces(4)
        : new Prisma.Decimal(0);

    // For last item, adjust to make total exactly 100%
    if (index === sortedTransactions.length - 1) {
      const adjustedPercentage = new Prisma.Decimal(100).minus(runningTotal);
      stakeholdersEquityMap[`${type}_${id}`] = {
        id,
        name,
        shareCount: transactionQuantity.toNumber(),
        sharePercentage: adjustedPercentage.toDecimalPlaces(4).toNumber(),
        type,
        capTableTransactionId: transaction.id,
        stakeholderId: transaction.stakeholding?.stakeholderId,
      };
    } else {
      runningTotal = runningTotal.plus(percentage);
      stakeholdersEquityMap[`${type}_${id}`] = {
        id,
        name,
        shareCount: transactionQuantity.toNumber(),
        sharePercentage: percentage.toNumber(),
        type,
        capTableTransactionId: transaction.id,
        stakeholderId: transaction.stakeholding?.stakeholderId,
      };
    }
  });

  return {
    dilutedShareCount: validDilutedShareCount,
    shareRetained,
    stakeholdersEquityMap,
    authorizedShares,
  };
};

const extractCorporateShareholders = (
  capTableTransactions: SerializedCapTableTransactionType[] | undefined
) => {
  if (!capTableTransactions) return [];
  return capTableTransactions
    ?.filter(
      (transaction) =>
        transaction.stakeholding?.stakeholder?.type ===
        StakeholderType.INSTITUTION
    )
    ?.map((transaction) => transaction.stakeholding?.stakeholder?.corporation)
    ?.filter(Boolean);
};

export { calculateStakeholdersEquityMap, extractCorporateShareholders };
